/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

import React, { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";
import AssistantHeader from "./AssistantHeader";
import ThreadView from "./ThreadView";
import Composer from "./Composer";
import HistoryModal from "./HistoryModal";
import AssistantSettingsModal from "./AssistantSettingsModal";
import { useAssistant } from "../AssistantContext";
import { SHAPE } from "../model/tokens";

/**
 * Persistent, NON-overlapping assistant.
 *
 * Rendered as an in-flow flex child of the row under the global navbar (see Body.jsx):
 *  - docked : a fixed-width column that shrinks the page content beside it.
 *  - focus  : full-screen — absolutely fills the row (under the navbar), covering the
 *             sidebar and page. Matches Shopify's expand behaviour.
 */
export default function AssistantPanel({ userName }) {
  const theme = useTheme();
  const { open, mode, closePanel, messages } = useAssistant();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (historyOpen) setHistoryOpen(false);
        else closePanel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, historyOpen, closePanel]);

  if (!open) return null;

  // Title: first user turn, truncated. Server-generated titles replace this later.
  const firstUser = messages.find((m) => m.role === "user");
  const title = firstUser
    ? firstUser.content.slice(0, 40) + (firstUser.content.length > 40 ? "…" : "")
    : "AI Assistant";

  const focus = mode === "focus";

  return (
    <Box
      role="dialog"
      aria-label="AI Assistant"
      sx={
        focus
          ? {
              position: "absolute",
              inset: 0,
              zIndex: 30,
              display: "flex",
              flexDirection: "column",
              backgroundColor: theme.palette.secondary.main,
            }
          : {
              flexShrink: 0,
              width: `${SHAPE.drawerWidth}px`,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              backgroundColor: theme.palette.secondary.main,
              borderLeft: `1px solid ${theme.palette.border.color}`,
            }
      }
    >
      <AssistantHeader
        title={title}
        onOpenHistory={() => setHistoryOpen(true)}
        onOpenSettings={() => setSettingsOpen(true)}
        centered={focus}
      />

      {/* In focus mode, centre the conversation to a readable column like Shopify */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          width: focus ? "min(820px, 100%)" : "100%",
          mx: focus ? "auto" : 0,
        }}
      >
        <ThreadView userName={userName} />
        <Composer />
      </Box>

      <HistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <AssistantSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </Box>
  );
}
